/**
 * Extend Stay Dialog Component
 * Allows front desk to extend a guest's checkout date
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Calendar, Info } from 'lucide-react';
import { toast } from 'sonner';

export const ExtendStayDialog = ({
  open,
  onOpenChange,
  guest,
  onConfirm,
  loading
}) => {
  const [newCheckOutDate, setNewCheckOutDate] = useState('');
  
  const handleSubmit = () => {
    if (!newCheckOutDate) {
      toast.error('Please select a new check-out date');
      return;
    }
    
    // Validate that new date is after current check-out date
    const currentCheckOut = new Date(guest?.check_out);
    const newDate = new Date(newCheckOutDate);
    
    if (newDate <= currentCheckOut) {
      toast.error('New check-out date must be after current check-out date');
      return;
    }
    
    onConfirm(newCheckOutDate);
  };
  
  const calculateAdditionalNights = () => {
    if (!newCheckOutDate || !guest?.check_out) return 0;
    
    const currentCheckOut = new Date(guest.check_out);
    const newDate = new Date(newCheckOutDate);
    const diffTime = newDate - currentCheckOut;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="extend-stay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Extend Guest Stay
          </DialogTitle>
        </DialogHeader>

        {guest && (
          <div className="space-y-4">
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                <p className="font-semibold mb-2">{guest.guest_name}</p>
                <div className="space-y-1 text-sm">
                  <p>Current Check-Out: <span className="font-semibold">{guest.check_out}</span></p>
                  <p>Room: {guest.room_type || guest.assigned_room_number || 'N/A'}</p>
                </div>
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="new-checkout-date">New Check-Out Date</Label>
              <Input
                id="new-checkout-date"
                type="date"
                value={newCheckOutDate}
                onChange={(e) => setNewCheckOutDate(e.target.value)}
                min={guest.check_out}
                data-testid="new-checkout-date-input"
              />
              <p className="text-xs text-muted-foreground">
                Select a date after {guest.check_out}
              </p>
            </div>

            {calculateAdditionalNights() > 0 && (
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-800">
                  <p className="font-semibold">
                    +{calculateAdditionalNights()} additional {calculateAdditionalNights() === 1 ? 'night' : 'nights'}
                  </p>
                  <p className="text-sm mt-1">
                    Guest will be moved back to the In-House list
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!newCheckOutDate || loading}
            data-testid="confirm-extend-stay-btn"
          >
            {loading ? 'Processing...' : 'Extend Stay'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
